import type { ComputedRef, Ref } from 'vue';
import type { LocationQuery } from 'vue-router';
import type { ResultItem } from '@/utils/file';
import type {
  InitialData as JobIdSearchSearchFormInitialData,
  ValidData as JobIdSearchSearchFormValidData,
} from '@/components/searchForms/JobIdSearchSearchForm.vue';
import type { DataItem as JobIdSearchTableDataItem } from '@/components/tables/JobIdSearchTable.vue';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { computed, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { isCancel } from 'axios';
import { format } from 'date-fns';
import _isEqual from 'lodash/isEqual';

import { useUserStore } from '@/stores/useUserStore';
import { fetchGetJobSearch, fetchGetJobSearchCount } from '@/helpers/fetchers';
import * as routeQueryHelpers from '@/helpers/routeQuery';
import * as jobIdSearchTableHelpers from '@/helpers/tables/jobIdSearch';
import * as routeNames from '@/constants/routeNames';

export interface JobIdSearchViewComposable {
  search: (formData: JobIdSearchSearchFormValidData) => void;
  refetch: () => Promise<void>;
  initialData: ComputedRef<JobIdSearchSearchFormInitialData>;
  isFetchingData: Ref<boolean>;
  isFetchingCountData: Ref<boolean>;
  total: Ref<number>;
  totalCount: Ref<number | undefined>;
  results: Ref<JobIdSearchTableDataItem[]>;
  resultsForCsv: Ref<ResultItem[]>;
}

export default function useJobIdSearchView(
  queryDates: Readonly<Ref<[Date, Date] | undefined>>,
  queryJobIds: Readonly<Ref<number[] | undefined>>,
  isStaff: Readonly<Ref<boolean>>,
): JobIdSearchViewComposable {
  const router = useRouter();
  const route = useRoute();
  const userStore = useUserStore();

  let controller: AbortController | undefined;
  let countController: AbortController | undefined;

  const userTimeZone = computed(() => userStore.user.timezone);

  const initialData = computed<JobIdSearchSearchFormInitialData>(() => {
    return {
      dates: queryDates.value,
      jobIds: '',
    };
  });

  const isFetchingData = ref(false);
  const isFetchingCountData = ref(false);
  const fetchedData = ref<openapi.GetJobSearch_1_200>();
  const fetchedCountData = ref<openapi.GetJobSearch_2_200>();
  const results = ref<JobIdSearchTableDataItem[]>([]);

  const total = computed(() => {
    return results.value.length;
  });

  const totalCount = computed(() => {
    if (results.value.length < 200) return;
    return fetchedCountData.value?.total_count;
  });

  const resultsForCsv = computed<ResultItem[]>(() => {
    return results.value;
  });

  function search(formData: JobIdSearchSearchFormValidData): void {
    const dates = [
      format(formData.dates[0], 'yyyy-MM-dd'),
      format(formData.dates[1], 'yyyy-MM-dd'),
    ].join(',');
    const jobIds =
      routeQueryHelpers.getQueryNumberListValue(
        formData.jobIds + ',' + (queryJobIds.value?.join(',') ?? ''),
      ) ?? [];
    const query = {
      ...route.query,
      dates,
      job_ids: jobIds.join(','),
    };
    const isReplace = _isEqual(route.query, query);
    void router.push({
      name: routeNames.SEARCH_JOB_ID,
      query,
      force: true,
      replace: isReplace,
    });
  }

  function clear(): void {
    if (controller) controller.abort();
    if (countController) countController.abort();
  }

  onUnmounted(() => {
    clear();
  });

  async function fetchData(newQuery: LocationQuery): Promise<void> {
    const dates = queryDates.value;
    const jobIds = queryJobIds.value;
    fetchedData.value = undefined;
    if (controller) controller.abort();
    if (!dates || !jobIds) return;
    isFetchingData.value = true;
    controller = new AbortController();
    try {
      const data = await fetchGetJobSearch(
        userTimeZone.value,
        {
          dates,
          jobIds,
        },
        {
          signal: controller.signal,
        },
      );
      if (!_isEqual(route.query, newQuery)) {
        return;
      }
      fetchedData.value = data;
    } catch (err: unknown) {
      if (!isCancel(err)) throw err;
    } finally {
      isFetchingData.value = false;
    }
  }

  async function fetchCountData(newQuery: LocationQuery): Promise<void> {
    const dates = queryDates.value;
    const jobIds = queryJobIds.value;
    fetchedCountData.value = undefined;
    if (countController) countController.abort();
    if (!dates || !jobIds) return;
    isFetchingCountData.value = true;
    countController = new AbortController();
    try {
      const data = await fetchGetJobSearchCount(
        userTimeZone.value,
        {
          dates,
          jobIds,
          count: true,
        },
        {
          signal: countController.signal,
        },
      );
      if (!_isEqual(route.query, newQuery)) {
        return;
      }
      fetchedCountData.value = data;
    } catch (err: unknown) {
      if (!isCancel(err)) throw err;
    } finally {
      isFetchingCountData.value = false;
    }
  }

  function updateResults(): void {
    if (!fetchedData.value) {
      results.value = [];
      return;
    }
    results.value = fetchedData.value.map((job) => {
      return jobIdSearchTableHelpers.getTableItem(job, isStaff.value);
    });
  }

  async function refetch(): Promise<void> {
    await fetchData(route.query);
    updateResults();
  }

  watch(
    route,
    async (newRoute) => {
      if (newRoute.name === routeNames.SEARCH_JOB_ID) {
        await fetchData(newRoute.query);
        updateResults();
        if (results.value.length === 200) {
          await fetchCountData(newRoute.query);
        }
      }
    },
    { immediate: true },
  );

  return {
    search,
    refetch,
    initialData,
    isFetchingData,
    isFetchingCountData,
    total,
    totalCount,
    results,
    resultsForCsv,
  };
}
